/**
 * @fileoverview Assemble a job report's HTML from drafted section rows.
 *
 * Shared by the generation action and the commit mutation so a finished
 * fan-out can be published without waiting on the (optional) coordinator
 * rewrite of the whole document.
 */

export interface AssembleSection {
  order: number;
  parentSectionId?: string;
  title: string;
  contentHtml?: string;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Concatenate sections in order. Top-level headings are h2; nested
 * (parented) headings are h3.
 */
export function assembleReport(sections: AssembleSection[]): string {
  const ordered = [...sections].sort((a, b) => a.order - b.order);
  const parts: string[] = [];
  for (const s of ordered) {
    const level = s.parentSectionId ? "h3" : "h2";
    parts.push(`<${level}>${escapeHtml(s.title)}</${level}>`);
    parts.push(s.contentHtml ?? "<p><em>No content for this section.</em></p>");
  }
  return parts.join("\n");
}

/** True when every section has a drafted (or annexure) body. */
export function allSectionsDrafted(
  sections: Array<{ status: string }>,
): boolean {
  return (
    sections.length > 0 &&
    sections.every((s) => s.status === "written" || s.status === "reviewed")
  );
}
